import { useCallback, useEffect, useRef, useState } from "react";

interface Column {
  key: string;
}

interface UseColumnVisibilityResult {
  visibleKeys: string[];
  toggle: (key: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
}

export function useColumnVisibility(
  storageKey: string,
  allColumns: Column[],
): UseColumnVisibilityResult {
  const allKeys = allColumns.map((c) => c.key);

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed: string[] = JSON.parse(stored);
        const valid = parsed.filter((k) => allKeys.includes(k));
        if (valid.length > 0) return valid;
      }
    } catch {
      // fall back to all visible
    }
    return allKeys;
  });

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
  }, [storageKey, visibleKeys]);

  useEffect(() => {
    if (!isOpen) return;
    function handleMouseDown(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const toggle = useCallback(
    (key: string) => {
      setVisibleKeys((prev) => {
        if (prev.includes(key) && prev.length === 1) return prev;
        if (prev.includes(key)) {
          return prev.filter((k) => k !== key);
        }
        return allColumns
          .map((c) => c.key)
          .filter((k) => k === key || prev.includes(k));
      });
    },
    [allColumns],
  );

  return { visibleKeys, toggle, isOpen, setIsOpen, dropdownRef };
}
